import { forwardRef, type HTMLAttributes, type ReactNode } from "react";

import { cn } from "../layout";
import {
  type AgentWorkbenchAppearanceOptions,
  useAgentWorkbenchAppearance,
} from "./appearance";

export interface AgentWorkbenchSidebarContainerClassNames {
  root?: string;
  header?: string;
  search?: string;
  primaryAction?: string;
  actions?: string;
  body?: string;
  groups?: string;
  footer?: string;
}

export interface AgentWorkbenchSidebarContainerBodyProps
  extends Omit<HTMLAttributes<HTMLDivElement>, "children" | "className"> {
  "data-agent-workbench-session-scroll-region"?: boolean | "";
}

export interface AgentWorkbenchSidebarContainerProps
  extends Omit<HTMLAttributes<HTMLElement>, "children" | "className">,
    AgentWorkbenchAppearanceOptions {
  children?: ReactNode;
  header?: ReactNode;
  search?: ReactNode;
  primaryAction?: ReactNode;
  actions?: ReactNode;
  groups?: ReactNode;
  footer?: ReactNode;
  className?: string;
  classNames?: AgentWorkbenchSidebarContainerClassNames;
  bodyProps?: AgentWorkbenchSidebarContainerBodyProps;
  bodyScrollable?: boolean;
  status?: "ready" | "loading" | "empty" | "error";
  loadingState?: ReactNode;
  emptyState?: ReactNode;
  errorState?: ReactNode;
}

/** Shared structural shell for agent navigation variants. Product data stays in the slots. */
export const AgentWorkbenchSidebarContainer = forwardRef<
  HTMLElement,
  AgentWorkbenchSidebarContainerProps
>(function AgentWorkbenchSidebarContainer(
  {
    children,
    header,
    search,
    primaryAction,
    actions,
    groups,
    footer,
    className,
    classNames,
    bodyProps,
    bodyScrollable = true,
    status = "ready",
    variant,
    density,
    loadingState,
    emptyState,
    errorState,
    ...props
  },
  ref,
) {
  const appearance = useAgentWorkbenchAppearance({ variant, density });
  const compact = appearance.density === "compact";
  const hasToolbar =
    search !== undefined ||
    primaryAction !== undefined ||
    actions !== undefined;
  const statusContent =
    status === "loading"
      ? loadingState
      : status === "empty"
        ? emptyState
        : status === "error"
          ? errorState
          : children;
  return (
    <nav
      ref={ref}
      data-agent-workbench-variant={appearance.variant}
      data-agent-workbench-density={appearance.density}
      data-agent-workbench-status={status}
      className={cn(
        "flex h-full min-h-0 flex-col bg-card text-card-foreground",
        classNames?.root,
        className,
      )}
      {...props}
    >
      {header !== undefined ? (
        <div className={cn("shrink-0", classNames?.header)}>{header}</div>
      ) : null}
      {hasToolbar ? (
        <div
          className={cn(
            "shrink-0",
            compact ? "space-y-2 px-3 pb-2 pt-3" : "space-y-3 px-4 pb-2 pt-4",
            classNames?.search,
          )}
        >
          {search}
          {primaryAction !== undefined ? (
            <div className={cn(classNames?.primaryAction)}>{primaryAction}</div>
          ) : null}
          {actions !== undefined ? (
            <div
              className={cn(
                "flex min-w-0 items-center gap-2",
                classNames?.actions,
              )}
            >
              {actions}
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        data-agent-workbench-scroll-region={bodyScrollable ? "" : undefined}
        {...bodyProps}
        tabIndex={bodyProps?.tabIndex ?? (bodyScrollable ? 0 : undefined)}
        className={cn(
          "min-h-0 flex-1",
          compact ? "px-3 pb-3" : "px-4 pb-4",
          bodyScrollable &&
            "overflow-x-hidden overflow-y-auto overscroll-contain",
          classNames?.body,
        )}
      >
        {statusContent}
      </div>
      {groups !== undefined ? (
        <div className={cn("shrink-0", classNames?.groups)}>{groups}</div>
      ) : null}
      {footer !== undefined ? (
        <div className={cn("shrink-0", classNames?.footer)}>{footer}</div>
      ) : null}
    </nav>
  );
});

AgentWorkbenchSidebarContainer.displayName = "AgentWorkbenchSidebarContainer";
